import { useCallback, useRef } from "react";
import { FileText, FileUp, Mail, X } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  MAX_SUPPORTING_DOCS,
  MAX_SUPPORTING_DOC_BYTES,
  type SupportingDocInput,
  type SupportingDocType,
} from "@/types";

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function isAllowedSupportingFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    file.type === "application/pdf" ||
    file.type === DOCX_MIME ||
    file.type === "text/plain" ||
    name.endsWith(".pdf") ||
    name.endsWith(".docx") ||
    name.endsWith(".txt")
  );
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

const DOC_TYPES: Array<{ type: SupportingDocType; label: string }> = [
  { type: "reference-contract", label: "Reference" },
  { type: "background", label: "Background" },
];

function sourceBadge(docType: SupportingDocType): string {
  return DOC_TYPES.find((item) => item.type === docType)?.label ?? "Background";
}

interface SupportingDocumentsUploaderProps {
  docs: SupportingDocInput[];
  onChange: (docs: SupportingDocInput[]) => void;
  maxDocs?: number;
  className?: string;
  onEmailClick?: () => void;
  /** When true, omit the section title/description (e.g. chat QuestionFlow already shows them). */
  hideHeader?: boolean;
}

export function SupportingDocumentsUploader({
  docs,
  onChange,
  maxDocs = MAX_SUPPORTING_DOCS,
  className,
  onEmailClick,
  hideHeader = false,
}: SupportingDocumentsUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const activeTypeRef = useRef<SupportingDocType>("background");

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const incoming = Array.from(files).filter(isAllowedSupportingFile);
      if (!incoming.length) return;

      const next: SupportingDocInput[] = [...docs];
      for (const file of incoming) {
        if (next.length >= maxDocs) break;
        if (file.size > MAX_SUPPORTING_DOC_BYTES) continue;
        next.push({
          id: crypto.randomUUID(),
          file,
          docType: activeTypeRef.current,
        });
      }
      onChange(next);
    },
    [docs, maxDocs, onChange]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) {
        addFiles(e.target.files);
        e.target.value = "";
      }
    },
    [addFiles]
  );

  const removeDoc = useCallback(
    (id: string) => {
      onChange(docs.filter((doc) => doc.id !== id));
    },
    [docs, onChange]
  );

  const canAddMore = docs.length < maxDocs;

  return (
    <div className={cn("space-y-3", className)}>
      {hideHeader ? null : (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-slate-200">
            Supporting Documents
            <span className="ml-1 text-xs font-normal text-slate-500">(Optional)</span>
          </label>
          <p className="text-xs text-[var(--lm-muted)] dark:text-slate-400">
            Choose a document type, then attach PDF, DOCX, or TXT files. Up to {maxDocs} file
            {maxDocs === 1 ? "" : "s"}, 10MB each.
          </p>
        </div>
      )}

      {canAddMore ? (
        <div className="flex flex-wrap gap-2">
          {DOC_TYPES.map((docType) => (
            <Button
              key={docType.type}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                activeTypeRef.current = docType.type;
                inputRef.current?.click();
              }}
              className="gap-2"
            >
              <FileUp className="h-3.5 w-3.5" />
              <span className="text-[10px] font-semibold uppercase tracking-wide">{docType.label}</span>
            </Button>
          ))}
          {onEmailClick ? (
            <Button type="button" variant="outline" size="sm" onClick={onEmailClick} className="gap-2">
              <Mail className="h-3.5 w-3.5" />
              <span className="text-[10px] font-semibold uppercase tracking-wide">Emails</span>
            </Button>
          ) : null}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".pdf,.docx,.txt"
            onChange={handleFileInput}
            className="hidden"
          />
        </div>
      ) : null}

      {docs.length > 0 ? (
        <ul className="space-y-2">
          {docs.map((doc) => (
            <li
              key={doc.id}
              className="flex flex-col gap-3 rounded-xl border border-slate-700 bg-slate-900/50 p-3 sm:flex-row sm:items-center"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-200">{doc.file.name}</p>
                  <p className="text-xs text-slate-500">{formatFileSize(doc.file.size)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:shrink-0">
                <span className="shrink-0 rounded-md bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700 dark:bg-violet-500/15 dark:text-violet-200">
                  {sourceBadge(doc.docType)}
                </span>
                <IconButton
                  size="sm"
                  variant="destructive"
                  title="Remove document"
                  onClick={() => removeDoc(doc.id)}
                >
                  <X className="h-4 w-4" />
                </IconButton>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
